import { RegionId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { useCallback } from "react";
import { useRegions } from "store/selectors/hooks";
import { officesPhrase } from "./business-profile-types";

export const useBusinessProfiles = () => {
    const trpcClient = trpc.useTRPC();
    const profilesQuery = useQuery(
        trpcClient.businessProfile.listProfiles.queryOptions({}),
    );
    const uncoveredOfficesQuery = useQuery(
        trpcClient.businessProfile.listUncoveredOffices.queryOptions({}),
    );
    return { profilesQuery, uncoveredOfficesQuery };
};

export type BusinessProfileOutput =
    inferRouterOutputs<AppRouter>["businessProfile"]["listProfiles"][number];

export type OfficeNameLookup = (regionId: RegionId) => string;

export const useRefreshBusinessProfiles = (): (() => Promise<void>) => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    return useCallback(
        () =>
            queryClient.invalidateQueries({
                queryKey: trpcClient.businessProfile.pathKey(),
            }),
        [queryClient, trpcClient],
    );
};

export const useStoreSavedBusinessProfile = (): ((
    saved: BusinessProfileOutput,
) => void) => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    return useCallback(
        (saved: BusinessProfileOutput) => {
            queryClient.setQueryData(
                trpcClient.businessProfile.listProfiles.queryKey({}),
                profiles => profiles?.map(p => (p.id === saved.id ? saved : p)),
            );
        },
        [queryClient, trpcClient],
    );
};

export const useCoveredOfficesPhrase = (): ((officeIds: RegionId[]) => string) => {
    const regions = useRegions();
    return useCallback(
        (officeIds: RegionId[]) => {
            const names = officeIds
                .map(regionId => regions.find(r => r.id === regionId)?.name)
                .filter((name): name is string => !!name);
            return officesPhrase(names, officeIds.length - names.length);
        },
        [regions],
    );
};

export const useOfficeName = (): OfficeNameLookup => {
    const regions = useRegions();
    return useCallback(
        (regionId: RegionId) => regions.find(r => r.id === regionId)?.name ?? "Another",
        [regions],
    );
};
